<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { useNotification } from '@kyvg/vue3-notification';
  import { carData } from '@/types';
  import {
    formatMoney,
    localstorageGet,
    localstorageSet,
  } from '@/helpers/helper';
  import analyticsCar from '@/helpers/analytics-car';
  import moment from 'moment';
  import { getSession } from '@/indexedDB';

  const analytics = ref(
    JSON.parse(localstorageGet('analytics')) ?? {
      histories: [],
      avgPrice: {},
      vinCode: '',
      lotDetails: {},
    }
  );
  const { notify } = useNotification();
  const dataCar = ref<carData | null>(null);
  const source = ref<string>(localstorageGet('auctionName'));
  const loader = ref<boolean>(false);
  const visibleVinDecodeBlock = ref<boolean>(false);
  const dataForAnalitycs = ref(dataCar.value?.info?.forAnalytics ?? {});
  const carAnalytics = async () => {
    if (dataCar.value?.info?.lotId) {
      const vinResponse = await analyticsCar.getVin(
        dataCar.value.info.lotId,
        dataCar.value.info.salvageId,
        source.value
      );
      analytics.value.vinCode = vinResponse.data.vin;
      dataCar.value.info.lastFourDigits = vinResponse.data.vin.replace(
        dataCar.value.info.vim,
        ''
      );
      analytics.value.lotDetails = await analyticsCar.getLotDetails(
        vinResponse.data.vin,
        dataCar.value.info.salvageId,
        source.value
      );
      analytics.value.histories = await analyticsCar.getVinHistories(
        vinResponse.data.vin
      );
      if (dataCar.value.info.typeEng !== 'ELECTRIC') {
        dataForAnalitycs.value.engine_volume =
          dataForAnalitycs.value?.engine_volume?.match(/(\d+\.\d+)/)[0] ?? 0;
      } else {
        delete dataForAnalitycs.value.engine_volume;
      }
      analytics.value.avgBids = await analyticsCar.getAvgBids(
        dataForAnalitycs.value
      );

      analytics.value.vinDecode = await analyticsCar.getVinDecoder(
        vinResponse.data.vin
      );
      localstorageSet('analytics', JSON.stringify(analytics.value));

      notify({
        title: 'Системне повідомлення!',
        type: 'success',
        text: "Аналіз машини завершенно, можете переглянути вкладку 'Аналітика Авто'!",
      });
    } else {
      notify({
        title: 'System alert!',
        type: 'warn',
        text: 'Please parse the car on the main page!',
      });
    }
  };
  onMounted(async () => {
    loader.value = true;
    dataCar.value = await getSession('car');
    dataForAnalitycs.value = dataCar.value?.info?.forAnalytics;
    if (!analytics.value.vinCode) {
      await carAnalytics();
    }
    loader.value = false;
  });
</script>

<template>
  <div
    v-if="dataCar"
    class="h-full w-full flex text-xl font-semibold text-gray-800 mb-4"
    :class="{
      'justify-center items-center': loader,
    }"
  >
    <ProgressSpinner v-if="loader"></ProgressSpinner>
    <div v-else class="w-full">
      <Card>
        <template #title>Auction Histories:</template>
        <template #content>
          <Carousel
            orientation="vertical"
            v-if="analytics.histories?.data"
            :value="analytics.histories?.data"
            :numVisible="1"
            :numScroll="1"
          >
            <template #item="slotProps">
              <div class="w-full space-y-2">
                <div class="flex justify-between items-center">
                  <div>Lot Number :</div>
                  <div>
                    {{ slotProps.data.all_lots_id }}
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Photos :</div>
                  <div>
                    <img
                      class="h-20"
                      :src="JSON.parse(slotProps.data.photo)[0]"
                      alt=""
                    />
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Status :</div>
                  <div>
                    {{ slotProps.data.sale_status }}
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Auction :</div>
                  <div>
                    {{ slotProps.data.auction_name }}
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Date :</div>
                  <div>
                    {{
                      moment(+slotProps.data.sale_date * 1000).format(
                        'DD.MM.YYYY'
                      )
                    }}
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Cost :</div>
                  <div>
                    {{ formatMoney(slotProps.data.purchase_price) }}
                  </div>
                </div>
                <div class="flex justify-between items-center">
                  <div>Seller :</div>
                  <div>
                    {{ slotProps.data.seller_api }}
                  </div>
                </div>
              </div>
            </template>
          </Carousel>
          <div v-else class="flex justify-center py-5 text-gray-500 font-bold">
            Auction Histories empty
          </div>
        </template>
      </Card>
      <Card class="stats-card mt-2" v-if="analytics.avgBids">
        <template #title>Last month's statistics</template>
        <template #content>
          <div>
            <div class="flex items-center justify-between">
              <span>Середня ціна</span>
              <span class="flex items-center gap-1">
                <i class="pi pi-arrow-up text-green-500"></i>
                $ {{ formatMoney(analytics.avgBids.data.avg) }}
              </span>
            </div>

            <!--            <div class="flex items-center justify-between">-->
            <!--              <span>Резерв продавця</span>-->
            <!--              <span>$5000.00</span>-->
            <!--            </div>-->

            <div class="flex items-center justify-between">
              <span>Min bid</span>
              <span class="flex items-center gap-1">
                <i class="pi pi-arrow-down text-pink-500"></i>
                $ {{ formatMoney(analytics.avgBids.data.min) }}
              </span>
            </div>

            <!-- 4. Аукціон -->
            <div class="flex items-center justify-between">
              <span>Max bid</span>
              <span class="flex items-center gap-1">
                <i class="pi pi-arrow-up text-green-500"></i>
                $ {{ formatMoney(analytics.avgBids.data.max) }}
              </span>
            </div>
          </div>
        </template>
      </Card>
      <div class="flex space-x-2 justify-between mt-2">
        <Button
          v-if="analytics?.vinCode?.length > 0"
          as="a"
          label="Remove Vin histories"
          :href="
            'https://hidecars.vin/en/?vin=' +
            analytics?.vinCode +
            '&utm_source=liontrans&utm_medium=referral&utm_campaign=liontst1&vin_clean_offer=i9dqdvqq'
          "
          target="_blank"
          rel="noopener"
        />
        <Button @click="visibleVinDecodeBlock = true">Vin Decoder</Button>
      </div>
    </div>
    <Drawer
      v-model:visible="visibleVinDecodeBlock"
      header="Vin Decode"
      position="right"
      class="!bg-gray-50"
    >
      <div class="flex flex-col gap-2">
        <div
          v-for="(decode, key) in Object.keys(analytics.vinDecode?.data ?? {})"
          :key="key"
        >
          <hr class="mb-2" />
          <div class="d-flex justify-between">
            <div class="font-bold min-w-[200px] text-left">{{ decode }}</div>
            <div>{{ analytics.vinDecode?.data[decode] }}</div>
          </div>
          <hr class="mt-2" />
        </div>
      </div>
    </Drawer>
  </div>
  <div
    v-else
    class="h-full w-full justify-center flex items-center text-xl font-semibold text-gray-800 mb-4"
  >
    <div class="text-center">
      This page shows analytics for the car you have parsed <br />You are not
      currently parsing the car
    </div>
  </div>
</template>

<style scoped></style>
